import { h } from 'preact';
import produce, { Draft } from 'immer';
import { SatisfactionCallbackResponse } from 'core_main/api/satisfaction/types';
import { useState, useContext } from 'preact/hooks';
import { getKustomerCore } from 'widget_main/globals/helpers';

import Button from 'widget_main/components/Button';
import SatisfactionMessageScale from 'widget_main/components/MessageThread/SatisfactionMessage/SatisfactionMessageScale';
import SatisfactionMessageModal from 'widget_main/components/MessageThread/SatisfactionMessage/SatisfactionMessageModal';
import { ConversationsContext } from 'widget_main/store';
import { useTranslations } from 'i18n/hooks';
import styles from './satisfactionMessage.scss';
import messages from './messages';

const kustomerCore = getKustomerCore();
interface SatisfactionMessageProps {
  satisfaction: SatisfactionCallbackResponse;
}

interface SatisfactionMessageFormState {
  rating?: number;
  followupAnswer?: string;
}

type ModalState = 'success' | 'feedback';

const SatisfactionMessage = ({ satisfaction }: SatisfactionMessageProps) => {
  const conversations = useContext(ConversationsContext);

  const translations = useTranslations(messages);

  const { satisfactionId, form, response } = satisfaction;

  const ratingPrompt = form?.ratingPrompt;
  const followupQuestion = form?.followupQuestion;
  const introduction = form?.introduction;

  const rating = response?.rating;
  const followupAnswer = response?.followupAnswer;

  const [
    satisfactionForm,
    setSatisfactionForm,
  ] = useState<SatisfactionMessageFormState>({
    rating,
    followupAnswer,
  });

  const [showModalType, setModalType] = useState<ModalState | undefined>(
    undefined,
  );

  const toggleFeedbackModal = () => {
    setModalType(() => 'feedback');
  };

  const toggleSuccessModal = () => {
    setModalType(() => 'success');
  };

  const setFollowupAnswer = (e?: Event) => {
    const target = e?.target as HTMLTextAreaElement;

    const value = target?.value;

    const newFollowupAnswer = value || followupAnswer;

    setSatisfactionForm(
      produce((draftState: Draft<SatisfactionMessageFormState>) => {
        draftState.followupAnswer = newFollowupAnswer;
      }),
    );
  };

  const closeModal = () => {
    setFollowupAnswer();
    setModalType(() => undefined);
  };

  const submit = (rating?: number) => {
    kustomerCore.submitSatisfactionForm(
      {
        satisfactionId,
        rating: rating || satisfactionForm.rating,
        followupAnswer: satisfactionForm.followupAnswer,
        submittedAt: new Date().toISOString(),
      },
      (response) => {
        if (satisfaction?.conversationId) {
          conversations.updateConversation(satisfaction.conversationId, {
            satisfaction: response,
          });
        }

        if (rating && followupAnswer) return;

        toggleSuccessModal();
      },
    );
  };

  const submitRating = (rating) => {
    submit(rating);
  };

  const submitFollowupAnswer = () => {
    submit();
  };

  const setRating = (rating) => {
    if (showModalType) return;

    setSatisfactionForm(
      produce((draftState: Draft<SatisfactionMessageFormState>) => {
        draftState.rating = rating;
      }),
    );

    if (followupQuestion && !satisfactionForm.followupAnswer) {
      toggleFeedbackModal();
      return;
    }

    submitRating(rating);
  };

  const renderFeedbackButton = () => {
    if (!followupAnswer) return null;

    return (
      <Button
        variant="link"
        block
        className={styles.feedbackButton}
        ariaLabel={translations.changeFeedbackButtonAriaLabel}
        onClick={toggleFeedbackModal}
      >
        <div className={styles.changeFeedback}>
          {translations.changeFeedbackButton}
        </div>
      </Button>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.satisfactionContainer}>
        <p className={styles.introduction}>{introduction}</p>
        <p className={styles.ratingPrompt}>{ratingPrompt}</p>
        <SatisfactionMessageScale
          rating={satisfactionForm?.rating}
          scale={satisfaction.form?.scale}
          onClick={setRating}
        />
      </div>
      {renderFeedbackButton()}
      <SatisfactionMessageModal
        modalContent={showModalType}
        onSubmit={submitFollowupAnswer}
        onHide={closeModal}
        setFollowupAnswer={setFollowupAnswer}
        followupAnswer={satisfactionForm.followupAnswer}
        followupQuestion={followupQuestion}
      />
    </div>
  );
};

SatisfactionMessage.defaultProps = {
  satisfaction: undefined,
};
export default SatisfactionMessage;
